import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'antd/es/form/Form';
import { trimValues } from '../../../utils/trimValues';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { useTranslation } from 'react-i18next';
import CollectionSessionInfoForm from './CollectionSessionInfoForm';
import { localDataService } from '../../../services/localDataService';
import { useLocation, useNavigate } from 'react-router-dom';
import BasicCard from '../../../components/basicCard/BasicCard';
import { updateTaskWithSync } from '../../../api/services/taskService';
import ScaleVerificationTable from './ScaleVerificationTable';
import Spin from 'antd/es/spin';
import SubmitScaleVerificationForm from './SubmitScaleVerification';
import dayjs from 'dayjs';
import { Steps } from 'antd';
import { XSquare } from 'react-bootstrap-icons';
import FormModelComponent from '../../../components/model/FormModelComponent';
import { useUpdateTaskStatusToInProgress } from '../../../hooks/task/useUpdateTaskStatusToInProgress';

const initialCollectionSessionInfo = {
  inspector: [],
  typeOfPosition: '',
  establishment: '',
  date: dayjs,
};

export const ScaleFormatVerificationForm: React.FC = () => {
  const navigate = useNavigate();
  const { updateTaskStatusToInProgress } = useUpdateTaskStatusToInProgress();

  const [taskData, setTaskData] = useState<any>();

  const { t } = useTranslation('inspection');

  const [collectionSessionInfoDataForm] = useForm();
  const [respondentDataForm] = useForm();

  const [collectionSessionInfo, setCollectionSessionInfo] = useState(initialCollectionSessionInfo);

  // const { IsAuthenticated, getUserToken } = useUserContext();
  const { successNotification, errorNotification } = useNotificationContext();
  const [triggeredFromTask, setTriggeredFromTask] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const respondentSectionRef = useRef<HTMLDivElement | null>(null);
  const [verificationTableData, setVerificationTableData] = useState({});
  const [showModalStep, setShowModalStep] = useState<number>(0);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const locationRoute = useLocation();

  const handleCancel = () => {
    respondentDataForm.resetFields();
    collectionSessionInfoDataForm.resetFields();

    if (triggeredFromTask) {
      const activeTab = taskData?.triggeredFrom === 'inspections' ? '2' : '1';
      navigate('/inspector', { state: { activeTab } });
    }
  };

  useEffect(() => {
    const taskData = locationRoute.state;
    if (taskData) {
      setTriggeredFromTask(true);
      updateTaskStatusToInProgress(taskData).then((updated) => {
        setTaskData(updated || taskData);
      });
    } else {
      setTaskData(undefined);
      setTriggeredFromTask(false);
    }
  }, [locationRoute.state, updateTaskStatusToInProgress]);

  const handleCreateScaleVerification = async () => {
    try {
      setIsLoading(true);

      const respondentFormData = respondentDataForm.getFieldsValue();
      const collectionSessionInfoValues = collectionSessionInfo;
      const trimmedRespondent = trimValues(respondentFormData);
      collectionSessionInfoDataForm.setFieldsValue(collectionSessionInfoValues);
      respondentDataForm.setFieldsValue(trimmedRespondent);

      const sessionData = {
        ...collectionSessionInfoValues,
        date: collectionSessionInfoValues?.date.valueOf(),
      };

      await Promise.all([
        collectionSessionInfoDataForm.validateFields(),
        respondentDataForm.validateFields(),
      ]);

      const inspectionBody = {
        date: Date.now(),
        locationId: taskData?.locationId,
        establishmentId: taskData?.establishmentId,
        establishmentOther: taskData?.establishmentOther,
        respondent: null,
        signatureInitial: null,
        finalRemarks: null,
        reportedTime: Date.now(),
        taskRefId: taskData?.refId,
        scaleVerificationDetails: {
          ...sessionData,
          verificationTableData: verificationTableData,
          ...trimmedRespondent,
        },
      };

      try {
        // successNotification('Saving price data...');
        const result = await localDataService.createLegalVerificationInspection(inspectionBody);

        if (result.inspection) {
          triggeredFromTask && updateTaskStatusToInProgress(taskData);
          successNotification(t('scaleVerificationSuccess'));
        }

        setCollectionSessionInfo(initialCollectionSessionInfo);

        collectionSessionInfoDataForm.resetFields();
        respondentDataForm.resetFields();
        handleCancel();
      } catch (error) {
        console.error('Failed to add scale verification:', error);
        errorNotification(`${t('scaleVerificationFailedAlert')}: ${error}`);
      } finally {
        setIsLoading(false);
      }
    } catch (validationError: any) {
      console.error('Form validation failed:', validationError.errorFields[0].errors[0]);
      errorNotification(`Form validation failed: ${validationError.errorFields[0].errors[0]}`);
      setIsLoading(false);
    }
  };

  const handleCollectionSessionInfoNext = async () => {
    const collectionSessionInfoFormData = collectionSessionInfoDataForm.getFieldsValue();

    const trimmedValues = trimValues(collectionSessionInfoFormData);
    collectionSessionInfoDataForm.setFieldsValue(trimmedValues);
    await collectionSessionInfoDataForm.validateFields();

    setCollectionSessionInfo({
      ...trimmedValues,
    });
    setShowModalStep(1);
  };

  const validateSecondStep = (data: any) => {
    setVerificationTableData(data);
    setTimeout(() => {
      respondentSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
    setShowModalStep(2);
  };

  const goBack = () => {
    setShowModalStep(showModalStep != 0 ? showModalStep - 1 : 0);
  };

  const toggleCancelModal = () => {
    isCancelModalOpen ? setIsCancelModalOpen(false) : setIsCancelModalOpen(true);
  };

  return (
    <>
      <FormModelComponent
        isOpen={isCancelModalOpen}
        header={t('verificationCancelModalTitle')}
        content={t('verificationCancelModalConfirmaiton')}
        onClickedCancel={toggleCancelModal}
        footerAction={{
          confimation: {
            label: t('yes'),
            onClick: () => {
              handleCancel();
            },
          },
          cancel: {
            label: t('no'),
            onClick: toggleCancelModal,
          },
        }}
        closeIcon={<XSquare className="text-primary-font-red" size={20} />}
        contentStyles="mb-4 px-2 max-h-[80vh] overflow-y-auto space-y-2"
      ></FormModelComponent>

      <div className="my-4 flex min-h-screen w-full gap-4">
        <div className="ml-0 mt-4 flex-1 overflow-y-auto rounded-lg md:ml-4">
          <BasicCard>
            <div className="mt-4 flex w-full justify-center">
              <div className="mt-4 gap-2 text-center">
                <h1 className="mb-2 text-2xl font-bold">{t('scaleFormatMainHeading')}</h1>
                <p className="mb-2">{t('scaleFormatSubHeading')}</p>
              </div>
            </div>

            <div className="relative p-4 md:p-6">
              {isLoading ? (
                <div className="black absolute z-10 flex h-full w-[calc(100%-50px)] flex-row items-center justify-center rounded-lg bg-white/60">
                  <Spin size={'large'} />
                </div>
              ) : null}
              <div className="flex items-center justify-center">
                <div className="m-0 w-full p-4 pb-0 md:m-4">
                  <div className="px-6 pb-5 pt-6 md:mb-4 md:mt-6 md:px-4">
                    <Steps
                      size="small"
                      current={showModalStep}
                      labelPlacement="vertical"
                      items={[
                        { title: t('generalInformation') },
                        { title: t('scaleFormatVerification') },
                        { title: t('signatureDetails') },
                      ]}
                      className="section-steps"
                    />
                  </div>

                  {showModalStep == 0 && (
                    <CollectionSessionInfoForm
                      form={collectionSessionInfoDataForm}
                      handleNext={handleCollectionSessionInfoNext}
                      handleCancel={toggleCancelModal}
                      isModal={false}
                      isCitizen={false}
                      triggeredFromTask={triggeredFromTask}
                      taskData={taskData}
                    />
                  )}

                  {showModalStep == 1 && (
                    <ScaleVerificationTable
                      submit={validateSecondStep}
                      handleBack={goBack}
                      handleCancel={toggleCancelModal}
                      initialValues={verificationTableData}
                      onValidationError={errorNotification}
                    />
                  )}

                  {showModalStep == 2 && (
                    <div ref={respondentSectionRef}>
                      <SubmitScaleVerificationForm
                        form={respondentDataForm}
                        handleSubmit={handleCreateScaleVerification}
                        handleCancel={toggleCancelModal}
                        handleBack={goBack}
                        disabled={isLoading}
                      ></SubmitScaleVerificationForm>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </BasicCard>
        </div>
      </div>
    </>
  );
};

export default ScaleFormatVerificationForm;
